import type { ReactNode } from "react";

import { cn } from "~/lib/utils";

interface SelectableCardProps {
    selected: boolean;
    onSelect: () => void;
    children: ReactNode;
    className?: string;
    /**
     * Watercolor illustration shown above the card's text. Omit for cards that carry no
     * artwork — the layout collapses rather than reserving an empty frame.
     */
    imageUrl?: string | null;
}

export function SelectableCard({
    selected,
    onSelect,
    children,
    className,
    imageUrl,
}: SelectableCardProps) {
    return (
        <button
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={onSelect}
            className={cn(
                // AIDEV-NOTE: `md:h-full`, NOT `h-full`. The card only needs to stretch
                // inside the desktop GRID, where its height means "fill this row". On
                // mobile the cards are a plain stack, so `height:100%` resolved against
                // the radiogroup — whose height is the whole stack — and every card tried
                // to be as tall as all of them, leaving huge empty space below the text
                // (Adam, 2026-09-09). Grid items already stretch by default; this is only
                // here so the inner flex column has a definite height to fill.
                "relative flex w-full flex-col overflow-hidden rounded-2xl border bg-white text-left transition-colors md:h-full",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-forest-500 focus-visible:ring-offset-2",
                selected
                    ? "border-brand-forest-500"
                    : "border-brand-forest-100 hover:border-brand-forest-300",
                className,
            )}
        >
            {/* AIDEV-NOTE: The selected ring is its own stacking layer, NOT `ring-inset` on the
                button. `ring-inset` is an inset box-shadow, which paints BELOW descendant
                content — so the full-bleed illustration covered the top edge of the ring and
                the selected card looked broken along its image. An overlay span sits above the
                image and is inert to pointer events, so the whole card is still one target. */}
            {selected ? (
                <span
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 z-10 rounded-2xl ring-2 ring-inset ring-brand-forest-500"
                />
            ) : null}
            {imageUrl ? (
                // AIDEV-NOTE: The resource watercolors sit on a warm ground that runs to the
                // edge of the frame, so the strip is cropped flush rather than padded — same
                // treatment as the Resources collection cards. aspect-[16/7] keeps the visual
                // centre (these are square originals) while staying a banner, not a hero.
                <span
                    aria-hidden="true"
                    className="block aspect-[16/7] w-full shrink-0 overflow-hidden bg-brand-cream-100"
                >
                    <img
                        src={imageUrl}
                        alt=""
                        loading="lazy"
                        className="size-full object-cover"
                    />
                </span>
            ) : null}
            {/* flex-col so a card can push its meta line to the bottom with mt-auto and
                still line up with a taller neighbour in the same grid row. */}
            <span className="relative flex flex-1 flex-col p-4 pr-12">
                <span
                    aria-hidden="true"
                    className={cn(
                        "absolute right-4 top-4 z-10 flex size-5 items-center justify-center rounded-full border bg-white",
                        selected
                            ? "border-brand-forest-700"
                            : "border-stone-300",
                    )}
                >
                    {selected ? (
                        <span className="size-2.5 rounded-full bg-brand-forest-700" />
                    ) : null}
                </span>
                {children}
            </span>
        </button>
    );
}
